// ======================================================================
// 📁 File: src/modules/ppe/pages/execution/ProductionExecutionDashboard.jsx
// ======================================================================
// Purpose:
//   Production Execution dashboard
//   UI intentionally mirrors Production Planning screen
//   Only data source + actions differ
// ======================================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import ppeExecutionService from "../../services/ppeExecutionService";

export default function ProductionExecutionDashboard() {

  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [summary, setSummary] = useState({
    total: 0,
    approved: 0,
    in_progress: 0
  });

  // ------------------------------------------------------------------
  // Load execution plans
  // ------------------------------------------------------------------
  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await ppeExecutionService.listExecutionPlans();

      setPlans(data);

      const total = data.length;
      const approved = data.filter(p => p.status === "APPROVED").length;
      const inProgress = data.filter(p => p.status === "IN_PROGRESS").length;

      setSummary({
        total,
        approved,
        in_progress: inProgress
      });

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  // ------------------------------------------------------------------
  // Filters
  // ------------------------------------------------------------------
  const filteredPlans = plans.filter(p => {

    const matchesSearch =
      p.plan_code?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      statusFilter === "ALL" || p.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // ------------------------------------------------------------------
  // UI
  // ------------------------------------------------------------------
  return (
    <div>

      {/* ============================================================= */}
      {/* HEADER */}
      {/* ============================================================= */}
      <div
        style={{
          background: "linear-gradient(90deg,#0ea5a4,#5eead4)",
          color: "#fff",
          padding: "18px 24px",
          borderRadius: 10,
          marginBottom: 20
        }}
      >
        <h2 style={{ margin: 0 }}>Production Execution</h2>
        <div style={{ opacity: 0.9 }}>
          Release production operations to shop floor
        </div>
      </div>

      {/* ============================================================= */}
      {/* KPI CARDS */}
      {/* ============================================================= */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 16,
          marginBottom: 20
        }}
      >
        <KpiCard title="Total Plans" value={summary.total} />
        <KpiCard title="Approved" value={summary.approved} color="#16a34a" />
        <KpiCard title="In Progress" value={summary.in_progress} color="#2563eb" />
        <KpiCard title="At Risk" value={0} color="#dc2626" />
      </div>

      {/* ============================================================= */}
      {/* FILTER BAR */}
      {/* ============================================================= */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 12
        }}
      >
        <input
          className="form-control"
          placeholder="Search Plan Code"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 200 }}
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="APPROVED">Approved</option>
          <option value="IN_PROGRESS">In Progress</option>
        </select>
      </div>

      {/* ============================================================= */}
      {/* TABLE */}
      {/* ============================================================= */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Plan Code</th>
                <th>Version</th>
                <th>Status</th>
                <th>Created At</th>
                <th width="120">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredPlans.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    No production plans found
                  </td>
                </tr>
              )}

              {filteredPlans.map(plan => (
                <tr key={plan.id}>
                  <td>{plan.plan_code}</td>
                  <td>{plan.plan_version}</td>
                  <td>
                    <span className="badge bg-success">
                      {plan.status}
                    </span>
                  </td>
                  <td>{plan.created_at}</td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() =>
                        navigate(`/ppe/execution/plans/${plan.id}`)
                      }
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      </div>

    </div>
  );
}

// ======================================================================
// KPI CARD
// ======================================================================

function KpiCard({ title, value, color = "#0f766e" }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 10,
        padding: 16,
        borderLeft: `5px solid ${color}`,
        boxShadow: "0 1px 4px rgba(0,0,0,.05)"
      }}
    >
      <div style={{ fontSize: 13, color: "#6b7280" }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
